import type { ZodType, ZodTypeDef } from 'zod'
import { requestExplorerJson } from '../request'

export type BlockscoutV2Client = {
  get: <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >(parameters: {
    path: string
    query?: Record<string, string>
    resultSchema: ZodType<Output, Def, Input>
    headers?: Record<string, string>
  }) => Promise<Output>
}

type CreateBlockscoutV2ClientParameters = {
  baseUrl: string
  proxyUrl?: string
}

/**
 * Blockscout V2 API 用の HTTP client を作成する。
 *
 * @param params.baseUrl - Blockscout Explorer の base URL
 * @param params.proxyUrl - Explorer API を中継するプロキシ URL
 * @returns Blockscout V2 API client
 */
export const createBlockscoutV2Client = ({
  baseUrl,
  proxyUrl,
}: CreateBlockscoutV2ClientParameters): BlockscoutV2Client => {
  const get = async <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >({
    path,
    query,
    resultSchema,
    headers,
  }: {
    path: string
    query?: Record<string, string>
    resultSchema: ZodType<Output, Def, Input>
    headers?: Record<string, string>
  }): Promise<Output> => {
    const url = new URL(path, baseUrl)
    if (query !== undefined) {
      for (const [key, value] of Object.entries(query)) {
        url.searchParams.append(key, value)
      }
    }
    console.debug(`[GET] ${url.toString()}`)
    return resultSchema.parse(
      await requestExplorerJson({
        url:
          proxyUrl === undefined
            ? url.toString()
            : `${proxyUrl}${encodeURIComponent(url.toString())}`,
        key: new URL(baseUrl).origin,
        intervalMs: 500,
        rateGroup: 'blockscout',
        headers,
      }),
    )
  }

  return {
    get,
  }
}
